/**
 * @flaha/shared-types — Subscription, entitlements, and usage (Phase 11).
 *
 * Commercial plan contracts. Scientific SoilTestLevel remains separate
 * from SubscriptionTier (see soil-test-level-standard.ts).
 */

import type { IsoDateString } from "./soil-domain";

// ---------------------------------------------------------------------------
// Enums (mirror prisma/v2-schema.prisma)
// ---------------------------------------------------------------------------

export enum AccountStatus {
	PENDING_EMAIL_VERIFICATION = "PENDING_EMAIL_VERIFICATION",
	ACTIVE = "ACTIVE",
	SUSPENDED = "SUSPENDED",
	CLOSED = "CLOSED",
}

export enum SubscriptionTier {
	FREE = "FREE",
	PROFESSIONAL = "PROFESSIONAL",
	ENTERPRISE = "ENTERPRISE",
}

export enum SubscriptionStatus {
	ACTIVE = "ACTIVE",
	TRIALING = "TRIALING",
	PAST_DUE = "PAST_DUE",
	PAYMENT_FAILED = "PAYMENT_FAILED",
	CANCELED = "CANCELED",
	EXPIRED = "EXPIRED",
	SUSPENDED = "SUSPENDED",
	PENDING_CONTRACT = "PENDING_CONTRACT",
}

export enum UsageType {
	TEST_CREATED = "TEST_CREATED",
	REPORT_GENERATED = "REPORT_GENERATED",
	MEMBER_ADDED = "MEMBER_ADDED",
	API_REQUEST = "API_REQUEST",
}

/** Stable entitlement keys (overrideable per subscription). */
export type EntitlementKey =
	| "MAX_LIFETIME_TESTS"
	| "MAX_TESTS_PER_BILLING_PERIOD"
	| "MAX_ORGANIZATIONS"
	| "MAX_PROJECTS"
	| "MAX_MEMBERS"
	| "ALLOW_PRELIMINARY"
	| "ALLOW_MODERATE"
	| "ALLOW_ADVANCED"
	| "ALLOW_STANDARD_REPORT"
	| "ALLOW_CUSTOM_REPORT"
	| "ALLOW_CUSTOM_BRANDING"
	| "ALLOW_ORG_INVITES"
	| "ALLOW_API_ACCESS";

export interface SubscriptionDTO {
	id: string;
	organizationId: string;
	tier: SubscriptionTier;
	status: SubscriptionStatus;
	grandfathered: boolean;
	currentPeriodStart: IsoDateString | null;
	currentPeriodEnd: IsoDateString | null;
	cancelAtPeriodEnd: boolean;
	contractReference: string | null;
	createdAt: IsoDateString;
	updatedAt: IsoDateString;
}

export interface EntitlementSnapshot {
	tier: SubscriptionTier;
	status: SubscriptionStatus;
	grandfathered: boolean;
	maxLifetimeTests: number | null;
	maxTestsPerBillingPeriod: number | null;
	maxOrganizations: number;
	maxProjects: number | null;
	maxMembers: number;
	allowPreliminary: boolean;
	allowModerate: boolean;
	allowAdvanced: boolean;
	allowStandardReport: boolean;
	allowCustomReport: boolean;
	allowCustomBranding: boolean;
	allowOrgInvites: boolean;
	allowApiAccess: boolean;
	billingPeriodKey: string | null;
	currentPeriodEnd: IsoDateString | null;
}

export interface EntitlementDecision {
	allowed: boolean;
	reasonCode?: string;
	message?: string;
	currentUsage?: number;
	limit?: number;
	upgradeRequired?: SubscriptionTier;
}

export interface OrgUsageSummaryDTO {
	organizationId: string;
	subscription: SubscriptionDTO | null;
	entitlements: EntitlementSnapshot;
	lifetimeTestsUsed: number;
	periodTestsUsed: number;
}

export interface GetOrgUsageResponse {
	usage: OrgUsageSummaryDTO;
}
